import { useEffect, useRef, useState } from "react";
import { Flame, Gift, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function StreakChip() {
  const { user, setUser } = useAuth();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    if (!user || done.current) return;
    done.current = true;
    api.post("/streak/checkin").then((r) => {
      setD((x) => ({ ...(x || {}), ...r.data }));
      if (r.data.user) setUser((u) => ({ ...u, login_streak: r.data.user.login_streak, perks: r.data.user.perks }));
      if (r.data.reward) toast.success(`Day ${r.data.reward.day} streak reward unlocked: ${r.data.reward.label}`, { duration: 7000 });
    }).catch(() => {});
    api.get("/streak").then((r) => setD((x) => ({ ...r.data, ...(x || {}) }))).catch(() => {});
  }, [user, setUser]);
  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  if (!user || !d) return null;
  const perks = d.perks_today || {};
  const hasPerk = (perks.picks || 0) + (perks.sgps || 0) > 0;
  return (
    <div className="relative" ref={ref}>
      <button data-testid="streak-chip" onClick={() => setOpen((o) => !o)} title={`${d.count}-day login streak`} className={`flex items-center gap-1 rounded-full border px-2 py-1 font-mono-num text-xs font-bold transition-colors ${d.count >= 3 ? "border-orange-500/50 bg-orange-500/10 text-orange-300" : "border-[#26334D] text-slate-400 hover:text-white"}`}>
        <Flame size={13} className={d.count >= 3 ? "fill-orange-400 text-orange-400" : ""} /> <span data-testid="streak-count">{d.count}</span>
        {hasPerk && <Gift size={12} className="text-emerald-400" data-testid="streak-perk-icon" />}
      </button>
      {open && (
        <div data-testid="streak-popover" className="glass-card fade-up absolute right-0 top-11 z-50 w-72 !bg-[#121824] p-4 shadow-2xl">
          <p className="font-display text-lg font-bold uppercase tracking-tight text-white">Day {d.count} streak</p>
          <p className="mt-0.5 text-xs text-slate-400">Best: {d.best} days · Show up daily to unlock perks.</p>
          <div className="mt-3 flex items-center gap-2 text-xs text-emerald-300"><Check size={13} /> Checked in today</div>
          {hasPerk && <p data-testid="streak-perk-active" className="mt-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200"><Gift size={12} className="mr-1 inline" /> Active today: {perks.label || `+${perks.picks} picks${perks.sgps ? ` & ${perks.sgps} SGP` : ""}`}</p>}
          <p className="mt-3 text-xs text-slate-300" data-testid="streak-next">Next perk in <span className="font-mono-num font-bold text-white">{d.days_to_next}</span> day{d.days_to_next !== 1 ? "s" : ""}: {d.next_reward}</p>
          {d.milestones && (
            <ul className="mt-3 space-y-1">
              {d.milestones.map((m) => (
                <li key={m.day} className={`flex items-center gap-2 text-[11px] ${d.count >= m.day ? "text-emerald-300" : "text-slate-500"}`}><span className="font-mono-num w-12">Day {m.day}</span><span>{m.label}</span>{d.count >= m.day && <Check size={11} className="ml-auto" />}</li>
              ))}
            </ul>
          )}
          {user.tier === "premium" && <p className="mt-3 text-[10px] text-slate-500">You're on Premium — every pick is already unlocked; streak perks stack as bragging rights on the crew board.</p>}
        </div>
      )}
    </div>
  );
}
